import '../App.css';
import axios from 'axios';
import { useEffect, useState, useRef } from 'react';
import Highlightable from 'highlightable';
import React from 'react';
import SelectionToolbar from './SelectionToolbar';
import { logHighlightEvent } from '../logging';
import { LowPassFilter, OneEuroFilter } from './OneEuroFilter';
import { ProgressCircle, Tooltip, Flex } from '@adobe/react-spectrum';

function TrailingWidgetDocument({ ...properties }) {
    // const [mouseWidgetInfo, setMouseWidgetInfo] = useState({ x: 0, y: 0, show: false, value: 0 });
    const [mouseWidgetPosition, setMouseWidgetPosition] = useState({ x: 0, y: 0 });
    const userNeedsToSlow = useRef(false);
    const userBubblePopped = useRef(false);
    const mouseIsDown = useRef(false);
    const mouseWidgetValueTimerRef = useRef(null);
    // const [mouseWidgetInstructions, setMouseWidgetInstructions] = useState("More highlighting decreases circle size. Keep your cursor in the circle.");

    // determined through Exp 1
    const MAX_CIRCLE_SIZE = 700;
    const MIN_CIRCLE_SIZE = 30;
    const IMPOSSIBLE_SIZE = 15; // the min size of the circle that is too small to be useful

    const MAX_HIGHLIGHT_WORDS = 150; // trying to force them to highlight only 55 words (roughly 10% of the docs)
    const extrapolate = true;

    const [tooltipOffset, setTooltipOffset] = useState({ x: MAX_CIRCLE_SIZE / 2, y: MAX_CIRCLE_SIZE / 2 });
    const tooltipRef = useRef(null);
    const mouseWidgetRef = useRef(null);
    const poppedTooltipRef = useRef(null);
    const [poppedTooltipOffset, setPoppedTooltipOffset] = useState({ x: 0, y: 0 });
    const [showPoppedTooltip, setShowPoppedTooltip] = useState(false);
    const mouseWidgetBounds = useRef({ width: MAX_CIRCLE_SIZE, height: MAX_CIRCLE_SIZE });
    const [potentialWordCount, setPotentialWordCount] = useState(0);
    const [mouseWidgetWidth, setMouseWidgetWidth] = useState(MAX_CIRCLE_SIZE);
    const [mouseWidgetWidthMultiplier, setMouseWidgetWidthMultiplier] = useState(1);
    const mouseWidgetWidthMultiplierRef = useRef(1); // animate function needs this to be a ref
    const [showMouseWidget, setShowMouseWidget] = useState(false);
    const poppingAnimationStartTime = useRef(0);
    const poppingAnimationEndTime = useRef(0);

    // One Euro Filter
    let frequency = 120; // Hz
    // const MAX_FC_MIN = 10.0;
    // const MIN_FC_MIN = 0.04;
    let fcMin = 1.0;
    let beta = 0;
    let dcutoff = 1.0;
    let alpha = 0.1;
    const filters = useRef({ fx: new OneEuroFilter(frequency, fcMin, beta, dcutoff), fy: new OneEuroFilter(frequency, fcMin, beta, dcutoff), diameter: new LowPassFilter(alpha) });
    // const filters = useRef({ fx: new LowPassFilter(0.5), fy: new LowPassFilter(0.5) });

    const mouseMovements = useRef([]);
    const [latestMousePosition, setLatestMousePosition] = useState({ x: 0, y: 0 });
    const latestFilteredMousePosition = useRef({ x: 0, y: 0 });
    const latestMousePositionRef = useRef({ x: 0, y: 0 }); // animate function needs this to be a ref
    const requestRef = useRef();
    const poppingRequestRef = useRef();
    const poppingMouseWidgetPosition = useRef({ x: 0, y: 0 });

    // animate the circle width changes
    const sizeRequestRef = useRef();
    const sizeChangeStartTime = useRef(0);
    const sizeChangeEndTime = useRef(0);

    const passageLength = properties.countNumWords(properties.passage);
    const highlightEventLogRef = useRef({});
    const mouseMoveEventsLogRef = useRef([]);

    function checkOverlappingRanges(left, right) {
        // the two ranges overlap
        if (right.start <= left.end) {
            return true
        }
        return false;
    }

    function mergeOverlappingRanges(left, right) {
        var newStart = Math.min(left.start, right.start);
        var newEnd = Math.max(left.end, right.end);
        var highlight = true;
        var showToolbar = false;

        // check whether the content should be highlighted right away
        if (properties.selectedTool !== "highlighter") {
            highlight = false;
            showToolbar = true;
        }
        return { start: newStart, end: newEnd, text: properties.passage.substring(newStart, newEnd + 1), highlight: highlight, showToolbar: showToolbar };
    }

    function mergeAllHighlights(newHighlight) {
        var highlights = properties.highlights.concat(newHighlight).sort((a, b) => a.start - b.start);

        function addDataToHighlight(highlightToEdit) {
            var highlight = true;
            var showToolbar = false;

            // check whether the content should be highlighted right away
            if (properties.selectedTool !== "highlighter") {
                highlight = false;
                showToolbar = true;
            }
            highlightToEdit.highlight = highlight;
            highlightToEdit.showToolbar = showToolbar;
        }

        for (var i = 0; i < highlights.length - 1; i++) {
            var left = highlights[i];
            var right = highlights[i + 1];
            if (checkOverlappingRanges(left, right)) {
                var merged = mergeOverlappingRanges(left, right);
                highlights.splice(i, 2, merged);
                i--;
            }
        }

        for (var i = 0; i < highlights.length; i++) {
            if (highlights[i].highlight === undefined || highlights[i].showToolbar === undefined) {
                addDataToHighlight(highlights[i]);
            }
        }


        return highlights;
    }

    function getHighlightedText(highlights) {
        var text = "";
        for (var i = 0; i < highlights.length; i++) {
            text += highlights[i].text + " ";
        }
        return text;
    }

    function checkRangeValid(range) {
        var rangeContents = document.getSelection().getRangeAt(0).cloneContents();
        var allSpans = rangeContents.querySelectorAll('span');
        var selectionSpans = [];
        allSpans.forEach((span) => {
            if (span.getAttribute('data-position') !== null) {
                selectionSpans.push(span);
            }
        });

        var startSpan = selectionSpans[0];
        var endSpan = selectionSpans[selectionSpans.length - 1];
        if (startSpan === undefined || endSpan === undefined) {
            return range;
        }

        // first check for any NaN values
        if (isNaN(range.start) || isNaN(range.end)) {
            var startIndex = parseInt(startSpan.getAttribute('data-position'));
            var endIndex = parseInt(endSpan.getAttribute('data-position'));
            range.start = startIndex;
            range.end = endIndex;
            range.text = properties.passage.substring(startIndex, endIndex + 1);
        }
        return range;
    }


    function onTextHighlighted() {
        // the popped the bubble, so don't make a new highlight
        if (userNeedsToSlow.current && userBubblePopped.current) {
            return;
        }

        var sel = document.getSelection();
        var unsnappedStartIndex = parseInt(sel.anchorNode.parentNode.getAttribute('data-position'));
        var unsnappedEndIndex = parseInt(sel.focusNode.parentNode.getAttribute('data-position'));
        var unsnappedText = properties.passage.substring(unsnappedStartIndex, unsnappedEndIndex + 1);
        var unsnappedRange = { start: unsnappedStartIndex, end: unsnappedEndIndex, text: unsnappedText };

        var wordSelection = snapSelectionToWord();
        var startNode = wordSelection.anchorNode.parentNode;
        var endNode = wordSelection.focusNode.parentNode;

        var startIndex = parseInt(startNode.getAttribute('data-position'));
        var endIndex = parseInt(endNode.getAttribute('data-position'));
        var leftToRight = startIndex < endIndex;
        // swap order if not left to right
        if (!leftToRight) {
            var tmpNode = endNode;
            endNode = startNode;
            startNode = tmpNode;

            var tmpIndex = endIndex;
            endIndex = startIndex;
            startIndex = tmpIndex;
        }

        var snappedRange = { start: startIndex, end: endIndex, text: properties.passage.substring(startIndex, endIndex + 1) };
        checkRangeValid(snappedRange);
        var mergedHighlights = mergeAllHighlights(snappedRange);
        var highlightedText = getHighlightedText(mergedHighlights);
        var newWordCount = properties.countNumWords(highlightedText);
        var validHighlight = true;

        if (newWordCount <= properties.maxWordCount || !properties.constrainHighlights) {
            // do not change the word count with the cursor tool, otherwise you double count
            if (properties.selectedTool === "highlighter") {
                properties.setWordCount(newWordCount);
            }
            properties.setHighlights(mergedHighlights);
            properties.setShowError(false);
            document.getSelection().removeAllRanges();
            validHighlight = true;
        }

        else if (newWordCount > properties.maxWordCount) {
            properties.setShowError(true);
            validHighlight = false;
        }

        let log = {
            timestamp: Date.now(),
            type: "add",
            workerId: properties.workerId,
            participantId: properties.participantId,
            experiment: properties.experiment,
            condition: properties.condition,

            unsnappedRange: unsnappedRange,
            snappedRange: snappedRange,
            newWordCount: newWordCount,
        }
        // console.log(log);
        logHighlightEvent(log);
    }

    // helper function to snap the selection to words - https://stackoverflow.com/questions/7380190/select-whole-word-with-getselection
    function snapSelectionToWord() {
        var sel;

        // Check for existence of window.getSelection() and that it has a
        // modify() method. IE 9 has both selection APIs but no modify() method.
        if (window.getSelection && (sel = window.getSelection()).modify) {
            sel = window.getSelection();
            if (!sel.isCollapsed) {
                // Detect if selection is backwards
                var range = document.createRange();
                range.setStart(sel.anchorNode, sel.anchorOffset);
                range.setEnd(sel.focusNode, sel.focusOffset);
                var backwards = range.collapsed;
                range.detach();

                // modify() works on the focus of the selection
                var endNode = sel.focusNode,
                    endOffset = sel.focusOffset;
                sel.collapse(sel.anchorNode, sel.anchorOffset);

                var direction = [];
                if (backwards) {
                    direction = ['backward', 'forward'];
                } else {
                    direction = ['forward', 'backward'];
                }

                sel.modify("move", direction[0], "character");
                sel.modify("move", direction[1], "word");
                sel.extend(endNode, endOffset);
                sel.modify("extend", direction[1], "character");
                sel.modify("extend", direction[0], "word");

            }
        } else if ((sel === document.selection) && sel.type !== "Control") {
            var textRange = sel.createRange();
            if (textRange.text) {
                textRange.expand("word");
                // Move the end back to not include the word's trailing space(s),
                // if necessary
                while (/\s$/.test(textRange.text)) {
                    textRange.moveEnd("character", -1);
                }
                textRange.select();
            }
        }

        return sel;
    }

    function filterSelections(element) {
        return element.highlight;
    }

    // helper function to count the number of potential words after a selection, without double counting existing highlights
    function countNumPotentialWords() {
        var sel = document.getSelection();
        if (sel.toString() === "") {
            return properties.wordCount;
        }

        else {
            var unsnappedStartIndex = parseInt(sel.anchorNode.parentNode.getAttribute('data-position'));
            var unsnappedEndIndex = parseInt(sel.focusNode.parentNode.getAttribute('data-position'));
            var unsnappedText = properties.passage.substring(unsnappedStartIndex, unsnappedEndIndex + 1);
            var unsnappedRange = { start: unsnappedStartIndex, end: unsnappedEndIndex, text: unsnappedText };

            var wordSelection = snapSelectionToWord();
            var startNode = wordSelection.anchorNode.parentNode;
            var endNode = wordSelection.focusNode.parentNode;

            var startIndex = parseInt(startNode.getAttribute('data-position'));
            var endIndex = parseInt(endNode.getAttribute('data-position'));
            var leftToRight = startIndex < endIndex;
            // swap order if not left to right
            if (!leftToRight) {
                var tmpNode = endNode;
                endNode = startNode;
                startNode = tmpNode;

                var tmpIndex = endIndex;
                endIndex = startIndex;
                startIndex = tmpIndex;
            }

            var snappedRange = { start: startIndex, end: endIndex, text: properties.passage.substring(startIndex, endIndex + 1), blurAmount: 0 };
            checkRangeValid(snappedRange);
            var mergedHighlights = mergeAllHighlights(snappedRange);
            var highlightedText = getHighlightedText(mergedHighlights);
            var newWordCount = properties.countNumWords(highlightedText);
            return newWordCount;
        }




        // return properties.wordCount + properties.countNumWords(sel.toString());
    }

    // filter out the selections that are not highlighted
    function mouseDown(e) {
        if (e.target.className !== "blackButton") {
            // hide temporary highlights
            var filteredHighlights = properties.highlights.filter(filterSelections);

            // hide all black buttons
            for (var i = 0; i < filteredHighlights.length; i++) {
                filteredHighlights[i].showToolbar = false;
            }

            properties.setHighlights(filteredHighlights);
            properties.setShowError(false);
        }

        // disable double clicking to highlight words and paragraphs
        if (e.detail > 1) {
            e.preventDefault();
            return;
        }

        setShowPoppedTooltip(false);
        mouseWidgetRef.current.style.backgroundColor = "black";
        document.getSelection().removeAllRanges();
        mouseMoveEventsLogRef.current = [];

        // only show the mouse widget if the user is not clicking on an existing highlight
        if (e.target.getAttribute('range') === null && e.target.className !== "blackButton") {
            mouseIsDown.current = true;
            userBubblePopped.current = false;

            mouseWidgetWidthMultiplierRef.current = 1;
            setMouseWidgetWidthMultiplier(1);

            // don't use potential word count here, because it's not updated as yet
            if (properties.wordCount > properties.targetWordCount) {
                customizeCircle(properties.wordCount);
                customizeTooltip();

                userNeedsToSlow.current = true;
                setShowMouseWidget(true);

                // clear the mouse movements
                mouseMovements.current = [{ x: e.clientX, y: e.clientY, added: "mouseDown" }];
                setMouseWidgetPosition({ x: e.clientX - (mouseWidgetBounds.current.width / 2), y: e.clientY - (mouseWidgetBounds.current.height / 2) });


            }

            // the size of the circle - the useState hasn't been updated yet, so just calculate it here
            let mWidth = mapRange(properties.wordCount, properties.targetWordCount, MAX_HIGHLIGHT_WORDS, MAX_CIRCLE_SIZE, MIN_CIRCLE_SIZE);
            if (extrapolate) {
                mWidth = Math.max(IMPOSSIBLE_SIZE, mWidth);
            }

            highlightEventLogRef.current = {
                type: "highlight",
                workerId: properties.workerId,
                participantId: properties.participantId,
                condition: properties.condition,
                experiment: properties.experiment,

                mouseDownTime: Date.now(),
                mouseDownX: e.clientX,
                mouseDownY: e.clientY,
                mouseDownWordCount: properties.wordCount,
                mouseDownStrength: mWidth, // size of the circle
            }


        }
    }

    function mouseUp(e) {
        if (e.detail > 1) {
            e.preventDefault();
            return; // prevent double click from triggering mouse up
        }
        mouseIsDown.current = false;
        setShowMouseWidget(false);
        clearInterval(mouseWidgetValueTimerRef.current);
        cancelAnimationFrame(sizeRequestRef.current);

        if (mouseMoveEventsLogRef.current.length > 0) {
            let sel = document.getSelection();
            let startNode = sel.anchorNode.parentNode;
            let endNode = sel.focusNode.parentNode;
            let unsnappedStartIndex = parseInt(startNode.getAttribute('data-position'));
            let unsnappedEndIndex = parseInt(endNode.getAttribute('data-position'));
            let unsnappedText = properties.passage.substring(unsnappedStartIndex, unsnappedEndIndex + 1);
            let unsnappedRange = { start: unsnappedStartIndex, end: unsnappedEndIndex, text: unsnappedText };

            highlightEventLogRef.current = {
                ...highlightEventLogRef.current,
                timestamp: Date.now(),
                mouseUpTime: Date.now(),
                mouseUpX: e.clientX,
                mouseUpY: e.clientY,

                mouseUpWordCount: potentialWordCount,
                mouseUpStrength: mouseWidgetWidth,
                userBubblePopped: userBubblePopped.current,

                mouseUpUnsnappedRange: unsnappedRange,
                mouseUpStartNodeBounds: startNode.getBoundingClientRect().toJSON(),
                mouseUpEndNodeBounds: endNode.getBoundingClientRect().toJSON(),

                mouseMoveEvents: mouseMoveEventsLogRef.current,
            }

            // console.log(highlightEventLogRef.current);
            logHighlightEvent(highlightEventLogRef.current);
        }
    }

    function mapRange(value, originalMin, originalMax, newMin, newMax) {
        let mappedValue = newMin + ((value - originalMin) * (newMax - newMin)) / (originalMax - originalMin);
        if (!extrapolate) {
            // if they are not extrapolating, then the value should be between the min and max wait times
            mappedValue = Math.min(newMax, Math.max(newMin, mappedValue));
        }
        return mappedValue;
    }

    function animateSizeChange() {
        let currTime = Date.now();
        if (sizeChangeStartTime.current === 0) {
            sizeChangeStartTime.current = currTime;
            sizeChangeEndTime.current = sizeChangeStartTime.current + 500; // 500ms second animation
        }

        let timeMapped = mapRange(currTime, sizeChangeStartTime.current, sizeChangeEndTime.current, 0, 1);
        customizeCircle(potentialWordCount);
        customizeTooltip();

        // can stop animating
        if (timeMapped >= 1) {
            sizeChangeStartTime.current = 0;
            sizeChangeEndTime.current = 0;
            cancelAnimationFrame(sizeRequestRef.current);
            sizeRequestRef.current = null;
        }
        else {
            sizeRequestRef.current = requestAnimationFrame(animateSizeChange);
        }
    }

    useEffect(() => {

        // show the mouse widget if the user is going too fast
        if (potentialWordCount > properties.targetWordCount && !userNeedsToSlow.current) {
            userNeedsToSlow.current = true;
            setShowMouseWidget(true);
        }

        else if (potentialWordCount <= properties.targetWordCount && userNeedsToSlow.current) {
            userNeedsToSlow.current = false;
            setShowMouseWidget(false);
        }

        // animate the size change
        if (sizeRequestRef.current === undefined || sizeRequestRef.current === null) {
            sizeRequestRef.current = requestAnimationFrame(animateSizeChange);
        }

        // if another animation is running, cancel it and start a new one
        else {
            cancelAnimationFrame(sizeRequestRef.current);
            sizeRequestRef.current = requestAnimationFrame(animateSizeChange);
        }

    }, [potentialWordCount]);

    function animate() {
        if (mouseMovements.current && mouseIsDown.current && userNeedsToSlow.current && !userBubblePopped.current) {
            let movementToFilter = mouseMovements.current.shift();

            if (!movementToFilter) {
                movementToFilter = latestMousePositionRef.current;
            }

            let filteredX = filters.current.fx.filter(movementToFilter.x)
            let filteredY = filters.current.fy.filter(movementToFilter.y)

            latestFilteredMousePosition.current = { x: filteredX, y: filteredY };
            setMouseWidgetPosition({ x: filteredX - (mouseWidgetBounds.current.width / 2), y: filteredY - (mouseWidgetBounds.current.height / 2) });
        }

        else if (latestMousePositionRef.current && !userBubblePopped.current) {
            setMouseWidgetPosition({ x: latestMousePositionRef.current.x - (mouseWidgetBounds.current.width / 2), y: latestMousePositionRef.current.y - (mouseWidgetBounds.current.height / 2) });
        }
        requestRef.current = requestAnimationFrame(animate);
    }

    useEffect(() => {
        requestRef.current = requestAnimationFrame(animate);

        return () => {
            cancelAnimationFrame(requestRef.current);
        }
    }, []);

    // helper function to calculate the distance between two points
    function distance(x1, y1, x2, y2) {
        return Math.sqrt(((x2 - x1) * (x2 - x1)) + ((y2 - y1) * (y2 - y1)));
    }

    function popBubbleAnimation() {
        let currTime = Date.now();
        let mappedWidthMultiplier = mapRange(currTime, poppingAnimationStartTime.current, poppingAnimationEndTime.current, 1, 3);
        mappedWidthMultiplier = Math.min(3, mappedWidthMultiplier);
        mappedWidthMultiplier = Math.max(1, mappedWidthMultiplier);
        mouseWidgetWidthMultiplierRef.current = mappedWidthMultiplier;

        setMouseWidgetWidthMultiplier(mouseWidgetWidthMultiplierRef.current);
        let newWidth = mouseWidgetWidth * mouseWidgetWidthMultiplierRef.current;
        setMouseWidgetPosition({ x: poppingMouseWidgetPosition.current.x - (newWidth / 2), y: poppingMouseWidgetPosition.current.y - (newWidth / 2) });


        if (mouseWidgetWidthMultiplierRef.current >= 3) {
            cancelAnimationFrame(poppingRequestRef.current);
            poppingRequestRef.current = null;
            mouseWidgetRef.current.style.transition = "none";
        }

        else {
            poppingRequestRef.current = requestAnimationFrame(popBubbleAnimation);
        }

    }

    useEffect(() => {
        if (showMouseWidget) {

            mouseWidgetRef.current.style.transition = "none";
            mouseWidgetRef.current.style.opacity = 0.1;

            // clear the mouse movements
            mouseMovements.current = [{ x: latestMousePosition.x, y: latestMousePosition.y, added: "useEffect" }];
            let potentialWordCount = countNumPotentialWords();

            filters.current = { fx: new OneEuroFilter(frequency, fcMin, beta, dcutoff), fy: new OneEuroFilter(frequency, fcMin, beta, dcutoff), diameter: new LowPassFilter(alpha) };
            // filters.current = { fx: new LowPassFilter(0.5), fy: new LowPassFilter(0.5) };
            setMouseWidgetPosition({ x: latestMousePosition.x - (mouseWidgetBounds.current.width / 2), y: latestMousePosition.y - (mouseWidgetBounds.current.height / 2) });
            customizeCircle(potentialWordCount);
            customizeTooltip();
            userBubblePopped.current = false;
        }
        else {
            if (userBubblePopped.current) {
                poppingMouseWidgetPosition.current = latestFilteredMousePosition.current;
                poppingAnimationStartTime.current = Date.now();
                poppingAnimationEndTime.current = poppingAnimationStartTime.current + 500; // 1 second animation
                poppingRequestRef.current = requestAnimationFrame(popBubbleAnimation);

                mouseWidgetRef.current.style.transition = "opacity 0.5s ease-in";
            }

            mouseWidgetRef.current.style.opacity = 0;
        }
    }, [showMouseWidget]);

    function mouseMove(e) {
        let textSelection = document.getSelection().toString();
        setLatestMousePosition({ x: e.clientX, y: e.clientY });
        latestMousePositionRef.current = { x: e.clientX, y: e.clientY };

        mouseMovements.current.push({ x: e.clientX, y: e.clientY, added: "mouseMove" });

        if (mouseIsDown.current) {
            setPotentialWordCount(countNumPotentialWords());
        }

        // add the current mouse position to the list of mouse positions so we can filter them later
        if (mouseIsDown.current && textSelection.length > 0 && textSelection !== "") {
            // check if the mouse is within the circle
            let distanceFromCenter = distance(e.clientX, e.clientY, mouseWidgetPosition.x + (mouseWidgetBounds.current.width / 2), mouseWidgetPosition.y + (mouseWidgetBounds.current.height / 2));
            let sel = document.getSelection();
            let startNode = sel.anchorNode.parentNode;
            let endNode = sel.focusNode.parentNode;
            let unsnappedStartIndex = parseInt(startNode.getAttribute('data-position'));
            let unsnappedEndIndex = parseInt(endNode.getAttribute('data-position'));
            let unsnappedText = properties.passage.substring(unsnappedStartIndex, unsnappedEndIndex + 1);
            let unsnappedRange = { start: unsnappedStartIndex, end: unsnappedEndIndex, text: unsnappedText };

            if ((distanceFromCenter > mouseWidgetBounds.current.width / 2) && userNeedsToSlow.current && showMouseWidget) {
                userBubblePopped.current = true;
                setShowPoppedTooltip(true);
                setShowMouseWidget(false);
                mouseWidgetRef.current.style.backgroundColor = "red";

                // popped
                highlightEventLogRef.current = {
                    ...highlightEventLogRef.current,
                    mouseLeaveType: "pop",
                    mouseLeaveTime: Date.now(),
                    mouseLeaveX: e.clientX,
                    mouseLeaveY: e.clientY,

                    // information about the circle/condition
                    mouseLeaveWordCount: potentialWordCount,
                    mouseLeaveStrength: mouseWidgetWidth,

                    // information about the text that was highlighted
                    mouseLeaveUnsnappedRange: unsnappedRange,
                    mouseLeaveStartNodeBounds: startNode.getBoundingClientRect().toJSON(),
                    mouseLeaveEndNodeBounds: endNode.getBoundingClientRect().toJSON(),

                }

            }

            // log all mouse movements
            if (mouseMoveEventsLogRef.current.length === 0 || mouseMoveEventsLogRef.current[mouseMoveEventsLogRef.current.length - 1].wordCount !== potentialWordCount) {
                mouseMoveEventsLogRef.current.push({
                    timestamp: Date.now(),
                    x: e.clientX,
                    y: e.clientY,
                    wordCount: potentialWordCount,
                    circleSize: mouseWidgetWidth,
                    unsnappedRange: unsnappedRange,
                    startNodeBounds: startNode.getBoundingClientRect().toJSON(),
                    endNodeBounds: endNode.getBoundingClientRect().toJSON(),
                });
            }
        }
    }

    function customizeCircle(wordCount, filter = true) {
        if (!userBubblePopped.current) {
            let mWidth = 0;
            let filteredWidth = 0;

            // if they keep highlighting, extrapolate
            if (wordCount > MAX_HIGHLIGHT_WORDS && extrapolate) {
                mWidth = mapRange(wordCount, properties.targetWordCount, MAX_HIGHLIGHT_WORDS, MAX_CIRCLE_SIZE, MIN_CIRCLE_SIZE);
                mWidth = Math.max(IMPOSSIBLE_SIZE, mWidth);
                filteredWidth = filters.current.diameter.filter(mWidth);
                setMouseWidgetWidth(filteredWidth);
            }
            else {
                mWidth = mapRange(wordCount, properties.targetWordCount, MAX_HIGHLIGHT_WORDS, MAX_CIRCLE_SIZE, MIN_CIRCLE_SIZE);
                filteredWidth = filters.current.diameter.filter(mWidth);
                setMouseWidgetWidth(filteredWidth);
            }

            if (filter) {
                setMouseWidgetWidth(filteredWidth);
            }
            else {
                setMouseWidgetWidth(mWidth);
            }
        }
    }

    function customizeTooltip() {
        if (tooltipRef.current && mouseWidgetRef.current) {
            let tooltip = tooltipRef.current.UNSAFE_getDOMNode();
            let tooltipBounds = tooltip.getBoundingClientRect();

            setTooltipOffset({ x: (tooltipBounds.width / 2) - (mouseWidgetBounds.current.width / 2), y: /*(tooltipBounds.height/2) - (mouseWidgetBounds.current.height / 2)*/ 40 });
        }

        if (poppedTooltipRef.current) {
            let poppedTooltip = poppedTooltipRef.current.UNSAFE_getDOMNode();
            let poppedTooltipBounds = poppedTooltip.getBoundingClientRect();
            setPoppedTooltipOffset({ x: (poppedTooltipBounds.width / 2), y: (poppedTooltipBounds.height / 2) + 25 });

        }
    }

    useEffect(() => {
        let mWidgetBounds = mouseWidgetRef.current.getBoundingClientRect();
        if (mWidgetBounds.width > 0 && mWidgetBounds.height > 0) {
            mouseWidgetBounds.current = mWidgetBounds;
        }
        customizeTooltip();
    }, [mouseWidgetPosition, mouseWidgetWidth, mouseWidgetWidthMultiplier]);

    return (
        <>

            <div style={{ backgroundColor: "#f8f8f8" }} onMouseDown={e => mouseDown(e)} onCopy={e => e.preventDefault()} onMouseUp={e => mouseUp(e)} onMouseMove={e => mouseMove(e)}>
                <Tooltip UNSAFE_className="no-select" ref={poppedTooltipRef} isOpen={showPoppedTooltip} UNSAFE_style={{ textAlign: "center", display: "unset" }} left={latestMousePosition.x - poppedTooltipOffset.x} top={latestMousePosition.y - poppedTooltipOffset.y} placement='top' position={"fixed"} zIndex={10000}>Your cursor left the circle. Try again.</Tooltip>
                <Tooltip UNSAFE_className="no-select" ref={tooltipRef} isOpen={showMouseWidget && !showPoppedTooltip} UNSAFE_style={{ textAlign: "center", display: "unset" }} left={mouseWidgetPosition.x - tooltipOffset.x} top={mouseWidgetPosition.y - tooltipOffset.y} placement='top' position={"fixed"} zIndex={10000}>More highlighting decreases circle size.<br /> Keep your cursor in the circle.</Tooltip>
                <div ref={mouseWidgetRef} className='circle no-select' style={{ left: mouseWidgetPosition.x, top: mouseWidgetPosition.y, width: (mouseWidgetWidth * mouseWidgetWidthMultiplier), height: (mouseWidgetWidth * mouseWidgetWidthMultiplier) }} /*hidden={(!showMouseWidget || showPoppedTooltip) && poppingAnimationOver}*/></div>

                <Flex direction="row" justifyContent={'center'}>
                    <div id="textToRead" className='documentReader'>
                        <Highlightable
                            ranges={properties.highlights}
                            enabled={properties.maxWordCount === 0 ? false : true} // disable highlighting if the max word count is 0
                            highlightStyle={range => {
                                if (range.highlight) {
                                    return {
                                        backgroundColor: '#FDEAA3',
                                    }
                                }
                                else {
                                    return {
                                        backgroundColor: '#b3d8ff',
                                    }
                                }
                            }}
                            onTextHighlighted={onTextHighlighted}
                            text={properties.passage}
                            nodeRenderer={(index, range, text) => {
                                var currChar = text.charAt(index);
                                if (currChar === '\n') {
                                    return (<><span data-position={index} range={range}><br /><br /></span></>)
                                }
                                return (
                                    <span data-position={index} range={range}>{currChar}</span>
                                )
                            }}
                            rangeRenderer={range => {
                                // helper function to update the tool bar visibility
                                function updateShowToolbar(newValue) {
                                    var index = properties.highlights.indexOf(range[0].props.range);
                                    var newHighlight = properties.highlights[index];
                                    newHighlight.showToolbar = newValue;
                                    properties.setHighlights(properties.highlights.toSpliced(index, 1).concat(newHighlight));
                                }

                                function mouseEnter(e) {
                                    e.style.outline = "2px solid #337AC2";
                                    e.style.cursor = "pointer";
                                }

                                function mouseLeave(e) {
                                    if (range[0].props.range.highlight)
                                        e.style.outline = "2px solid #FDEAA3";
                                    else
                                        e.style.outline = "2px solid #b3d8ff";
                                    e.style.cursor = "auto";
                                }

                                function mouseClick(e) {
                                    updateShowToolbar(true);
                                }

                                // creating an actual highlight
                                if (range[0].props.range.highlight) {
                                    return (
                                        <div style={{ display: 'inline', position: 'relative' }}>
                                            <SelectionToolbar
                                                workerId={properties.workerId}
                                                participantId={properties.participantId}
                                                experiment={properties.experiment}
                                                condition={properties.condition}

                                                passage={properties.passage}
                                                countNumWords={properties.countNumWords}

                                                wordCount={properties.wordCount}
                                                setWordCount={properties.setWordCount}
                                                setProgressWordCount={properties.setProgressWordCount}

                                                selectedTool={'highlighter'}
                                                updateShowToolbar={updateShowToolbar}

                                                range={range}
                                                highlights={properties.highlights}
                                                setHighlights={properties.setHighlights} />
                                            <span style={{ outline: "2px solid #FDEAA3", backgroundColor: "#FDEAA3" }} onMouseEnter={e => mouseEnter(e.target.parentNode)} onMouseLeave={e => mouseLeave(e.target.parentNode)} onClick={e => mouseClick(e.target.parentNode)}>{range}</span>
                                        </div>
                                    );

                                }

                                // creating a fake selection effect
                                else {
                                    return (
                                        <div style={{ display: 'inline', position: 'relative' }}>
                                            <SelectionToolbar
                                                workerId={properties.workerId}
                                                participantId={properties.participantId}
                                                experiment={properties.experiment}
                                                condition={properties.condition}

                                                passage={properties.passage}
                                                countNumWords={properties.countNumWords}

                                                wordCount={properties.wordCount}
                                                setWordCount={properties.setWordCount}
                                                setProgressWordCount={properties.setProgressWordCount}
                                                setShadowWordCount={properties.setShadowWordCount}

                                                selectedTool={'cursor'}
                                                updateShowToolbar={updateShowToolbar}

                                                range={range}

                                                highlights={properties.highlights}
                                                setHighlights={properties.setHighlights} />
                                            <span style={{ outline: "2px solid #b3d8ff", backgroundColor: "#b3d8ff" }}>{range}</span>
                                        </div>)
                                        ;
                                }
                            }}
                        />

                    </div>
                </Flex>
            </div>

        </>
    );
}

export default TrailingWidgetDocument;